import {
  faChevronRight,
  faEarthAmericas,
  faListCheck,
  faFireFlameCurved,
  faUser,
  faPalette
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import React, { useMemo } from 'react';
import { Button } from 'react-bootstrap';
import Badge from '../../base/Badge';
import EditLotLength from './EditLotLength';
import {
  productColorName,
  productColorBadgeStyle
} from '../../../helpers/productColor';
import { sourceBreakdown, clientBadgeColor } from '../../../helpers/sourceBreakdown';
import { useJobOrdersSummary } from '../../../features/orders/useOrders';

const ProjectDetailsSummary = ({
  project,
  showTaskTable,
  setShowTaskTable,
  showOrdersTable,
  setShowOrdersTable
}) => {
  const tasksCount = useMemo(() => {
    const tasksObj = project?.tasks || {};
    return Object.keys(tasksObj).length;
  }, [project]);

  const allocationsCount = useMemo(() => {
    const arr = Array.isArray(project?.allocations) ? project.allocations : [];
    return arr.length;
  }, [project]);

  const toggleTasks = () => setShowTaskTable(v => !v);
  const toggleOrders = () => setShowOrdersTable(v => !v);

  // The colour of the CABLE — the sheath on a jacketed one, the wire on a
  // single-conductor one. It was on the overview nowhere, so the page could not
  // tell a WHITE MYYUP from a BLACK one, and the two are different products.
  const colorName = productColorName(project);
  const colorStyle = productColorBadgeStyle(colorName);

  // `MIXED` on its own is true and useless — it says some of the lot is
  // promised without saying how much, or to whom. Same query key the linked-
  // orders table already uses, so React Query serves it from cache rather than
  // asking twice.
  const { data: ordersSummary } = useJobOrdersSummary(project?.jobId);
  const split = useMemo(
    () => sourceBreakdown(project, ordersSummary?.rows || []),
    [project, ordersSummary]
  );
  const fmtM = m => `${Math.round(m).toLocaleString()} m`;

  return (
    <>
      <div className="mb-4 mb-xl-4">
        <table className="lh-sm mb-4 mb-sm-0 mb-xl-4">
          <tbody>
            <tr>
              <td className="align-top py-1">
                <div className="d-flex">
                  <FontAwesomeIcon
                    icon={faEarthAmericas}
                    className="me-2 text-700 fs-9"
                  />
                  <h5 className="text-900 mb-0 text-nowrap">Name :</h5>
                </div>
              </td>
              <td className="ps-1 py-1">
                <Link
                  className="fw-semi-bold d-block lh-sm"
                  to={`/apps/standard/${project.name}/${project.numberOfConductors}/${project.section}`}
                >
                  {project.productLabel}
                </Link>
              </td>
            </tr>

            <tr>
              <td className="align-top py-1">
                <div className="d-flex">
                  <FontAwesomeIcon
                    icon={faPalette}
                    className="me-2 text-700 fs-9"
                  />
                  <h5 className="text-900 mb-0 text-nowrap">Colour :</h5>
                </div>
              </td>
              <td className="ps-1 py-1">
                {colorStyle ? (
                  <span
                    className="badge fw-bold px-2 py-1"
                    style={colorStyle}
                    title={`Cable colour — ${colorName}`}
                  >
                    {colorName}
                  </span>
                ) : (
                  <span className="text-body-tertiary fst-italic fs-9">
                    not recorded
                  </span>
                )}
              </td>
            </tr>

            <tr>
              <td className="align-top py-1">
                <div className="d-flex">
                  <FontAwesomeIcon
                    icon={faFireFlameCurved}
                    className="me-2 text-700 fs-9"
                  />
                  <h5 className="text-900 mb-0 text-nowrap">Lot :</h5>
                </div>
              </td>
              <td className="ps-1 py-1">
                {project.plannedLength}
                <EditLotLength project={project} />
              </td>
            </tr>

            <tr>
              <td className="align-top py-1">
                <div className="d-flex">
                  <FontAwesomeIcon
                    icon={faUser}
                    className="me-2 text-700 fs-9"
                  />
                  <h5 className="text-900 mb-0 text-nowrap">Source :</h5>
                </div>
              </td>
              <td className="ps-1 py-1">
                {/* The word MIXED is gone: it only ever meant "read the badges
                    below", and they say it properly. A kind that is NOT mixed
                    is still worth naming — STOCK or ORDER tells you something
                    the badges would make you add up. */}
                {split.kind && split.kind !== 'MIXED' && (
                  <span className="fw-semi-bold d-block lh-sm text-700">{split.kind}</span>
                )}
                <div className="d-flex flex-wrap gap-1">
                  {split.stock > 0 && (
                    <Badge
                      bg="warning"
                      className="fs-10 fw-bold"
                      title="Not promised to anybody yet — free to allocate"
                    >
                      STOCK {fmtM(split.stock)}
                    </Badge>
                  )}
                  {/* One colour per client rather than one colour for "client":
                      with three orders on a lot, three identical blue badges
                      are a wall of text. The colour is derived from the name,
                      so the same client keeps the same colour on every product. */}
                  {split.clients.map(c => (
                    <Badge
                      key={`${c.client}-${c.orderCode || ''}`}
                      bg={clientBadgeColor(c.client)}
                      className="fs-10 fw-bold"
                      title={
                        c.orderCode
                          ? `Order ${c.orderCode} — ${c.client}`
                          : `Allocated to ${c.client}`
                      }
                    >
                      {c.client} {fmtM(c.meters)}
                    </Badge>
                  ))}
                  {split.stock <= 0 && !split.clients.length && (
                    <span className="fs-10 text-700">nothing allocated yet</span>
                  )}
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <table className="lh-sm">
          <tbody>
            <tr>
              <td className="align-top py-1 text-900 text-nowrap fw-bold">
                Started :
              </td>
              <td className="text-600 fw-semi-bold ps-3">
                {new Date(project.createdAt).toLocaleString()}
              </td>
            </tr>
            <tr>
              <td className="align-top py-1 text-900 text-nowrap fw-bold">
                Deadline :
              </td>
              <td className="text-600 fw-semi-bold ps-3">
                {project?.source?.dueDate ||
                  project?.source?.launch?.dueDate ||
                  '—'}
              </td>
            </tr>
            <tr>
              <td className="align-top py-1 text-900 text-nowrap fw-bold">
                Progress :
              </td>
              <td className="text-warning fw-semi-bold ps-3">
                {project.progress}%
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="mb-1">
        <div className="d-flex align-items-center">
          <FontAwesomeIcon icon={faListCheck} className="me-2 text-700 fs-9" />
          <h5 className="text-1100 mb-0 me-2">
            {tasksCount}
            <span className="text-900 fw-normal ms-2">tasks</span>
          </h5>

          <Button
            variant="link"
            className="fw-bold fs-9 mt-1 p-0"
            onClick={toggleTasks}
          >
            {showTaskTable ? 'Hide tasks' : 'See tasks'}{' '}
            <FontAwesomeIcon icon={faChevronRight} className="ms-1 fs-10" />
          </Button>
        </div>
      </div>

      <div className="mb-3">
        <div className="d-flex align-items-center">
          <FontAwesomeIcon icon={faListCheck} className="me-2 text-700 fs-9" />
          <h5 className="text-1100 mb-0 me-2">
            {allocationsCount}
            <span className="text-900 fw-normal ms-2">order allocations</span>
          </h5>

          <Button
            variant="link"
            className="fw-bold fs-9 mt-1 p-0"
            onClick={toggleOrders}
          >
            {showOrdersTable ? 'Hide orders' : 'Show orders'}{' '}
            <FontAwesomeIcon icon={faChevronRight} className="ms-1 fs-10" />
          </Button>
        </div>
      </div>
    </>
  );
};

export default ProjectDetailsSummary;
